import React from 'react';
import {
  Form,
  useSubmit,
  useActionData,
  useNavigation,
  useLoaderData,
  useNavigate,
  useRouteLoaderData,
  useLocation,
  useOutlet,
  NavLink
} from 'react-router-dom';

import * as Headless from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  DocumentPlusIcon,
  CalendarIcon,
  PencilIcon,
  UserPlusIcon,
  InboxIcon,
  UserIcon,
  BuildingOfficeIcon,
  EyeIcon,
  CloudArrowDownIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import {
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { PageTitle } from '@oysterjs/ui/page';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@oysterjs/ui/table';
// import {
//   Pagination,
//   PaginationPrevious,
//   PaginationList,
//   PaginationPage,
//   PaginationGap,
//   PaginationNext
// } from '@oysterjs/ui/pagination';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend
} from '@oysterjs/ui/fieldset';
import { PageNavigation } from '@oysterjs/ui/navigation';
import {
  AccountBusinessPolicyFieldsFragment,
  AccountCertificateOfLiabilityInsuranceFieldsFragment,
  AccountContactFieldsFragment,
  AccountFieldsFragment,
  AccountServiceRequestFieldsFragment,
  AccountType,
  CertificateOfLiabilityInsuranceFieldsFragment,
  CreateAccountInput,
  CreateAccountMutation,
  CreateCertificateInput,
  CreateCertificateMutation,
  PolicyState,
  UpdateAccountInput,
  UpdateAccountMutation,
  useGetContactsLazyQuery
} from '~/api/graphql';
import { ValidationError } from '@oysterjs/core/graphql';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { AddressForm } from '@oysterjs/ui/address';
import { clsx } from 'clsx';
import { Combobox } from '@oysterjs/ui/combobox';
import { CheckboxField, Checkbox } from '@oysterjs/ui/checkbox';
import { getDisplayInsuranceType } from '~/lib/insurance';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Badge } from '@oysterjs/ui/badge';
import { Textarea } from '@oysterjs/ui/textarea';
import { Spinner } from '@oysterjs/ui/spinner';

export const AccountsPage = () => {
  const outlet = useOutlet();
  const location = useLocation();

  const [filter, setFilter] = React.useState('');
  const loader = useLoaderData() as
    | { data: Promise<{ accounts?: AccountFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Accounts" description="View and manage your agency's accounts">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full max-w-md">
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label className="sr-only" data-slot="none">
                      Filter table
                    </Label>
                    <InputGroup>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      <Input
                        type="search"
                        name="search"
                        placeholder="Start typing to filter..."
                        value={filter}
                        onChange={(e) => setFilter(e.currentTarget.value)}
                      />
                    </InputGroup>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </form>
            <Button color="sky" href="/app/accounts/new">
              Create Account
            </Button>
          </div>
        </div>
        <React.Suspense fallback={<AccountsPageLoader />}>
          <Await resolve={loader?.data}>
            {(data) =>
              data?.accounts && (
                <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
                  {!data.accounts?.length && (
                    <div className="text-center py-12">
                      <UserPlusIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
                      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                        No accounts found
                      </h3>
                      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                        You haven't added any accounts yet. Create a new account to get started.
                      </p>
                    </div>
                  )}
                  {!!data.accounts?.length && (
                    <Table
                      dense
                      bleed
                      className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                    >
                      <TableHead>
                        <TableRow>
                          <TableHeader className="py-4">Name</TableHeader>
                          <TableHeader className="py-4">Address</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.accounts
                          ?.filter(
                            (account) =>
                              !filter ||
                              JSON.stringify(account)
                                .toLowerCase()
                                .indexOf(filter.toLowerCase()) !== -1
                          )
                          .map((account) => (
                            <TableRow key={account.id} href={`/app/accounts/${account.id}`}>
                              <TableCell>
                                <div className="font-medium flex gap-x-4">
                                  <span>{account.account.name}</span>
                                  <Badge
                                    color={account.type === AccountType.Business ? 'sky' : 'indigo'}
                                  >
                                    {account.type === AccountType.Business
                                      ? 'Business'
                                      : 'Individual'}
                                  </Badge>
                                </div>
                                <div className="text-neutral-500 dark:text-neutral-400">
                                  <a
                                    href="#"
                                    className="hover:text-neutral-700 dark:hover:text-neutral-300"
                                  >
                                    {account.account.email}
                                  </a>
                                </div>
                              </TableCell>
                              <TableCell>{account.account.mailingAddress.line1}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )
            }
          </Await>
        </React.Suspense>
      </div>
      {/* <Pagination className="mt-6">
        <PaginationPrevious href="?page=2" />
        <PaginationList>
          <PaginationPage href="?page=1">1</PaginationPage>
          <PaginationPage href="?page=2">2</PaginationPage>
          <PaginationPage href="?page=3" current>
            3
          </PaginationPage>
          <PaginationPage href="?page=4">4</PaginationPage>
          <PaginationGap />
          <PaginationPage href="?page=65">65</PaginationPage>
          <PaginationPage href="?page=66">66</PaginationPage>
        </PaginationList>
        <PaginationNext href="?page=4" />
      </Pagination> */}

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </PageTitle>
  );
};

export const AccountsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading accounts...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

export const AccountCreatePage = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateAccountMutation>;

  const [fetchContacts, { data: contacts, loading: contactsLoading, error: contactsError }] =
    useGetContactsLazyQuery();

  const [contactCreationMode, setContactCreationMode] = React.useState<'new' | 'existing'>('new');
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateAccountInput>();
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (contactCreationMode === 'existing') {
      fetchContacts();
    }
  }, [contactCreationMode]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="2xl">
      <DialogTitle>Create a new account</DialogTitle>
      <DialogDescription>Create a new account and associate it with a contact.</DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
          <Fieldset>
            <Legend>Account Type</Legend>
            <Description>Select the type of account to create.</Description>
            <FieldGroup>
              <Field>
                <Headless.RadioGroup
                  data-slot="control"
                  value={form?.type || ''}
                  onChange={(type) =>
                    updateForm(['type'], (prev) => ({
                      ...prev,
                      type: type as AccountType,
                      contact:
                        type === AccountType.Individual
                          ? undefined
                          : {
                              name: '',
                              email: '',
                              phone: ''
                            },
                      business:
                        prev?.business ||
                        (type === AccountType.Business
                          ? {
                              name: '',
                              dba: '',
                              email: '',
                              phone: '',
                              mailingAddress: {
                                line1: '',
                                city: '',
                                zone: '',
                                postalCode: ''
                              },
                              website: ''
                            }
                          : undefined),
                      individual:
                        prev?.individual ||
                        (type === AccountType.Individual
                          ? {
                              name: '',
                              email: '',
                              phone: '',
                              mailingAddress: {
                                line1: '',
                                city: '',
                                zone: '',
                                postalCode: ''
                              }
                            }
                          : undefined)
                    }))
                  }
                >
                  <div className="grid grid-cols-2 gap-4">
                    <Headless.Radio
                      value={AccountType.Business}
                      aria-label="Business Account"
                      aria-description="An account for a business with commercial policies."
                      className={clsx([
                        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
                        'group relative flex cursor-pointer rounded-lg bg-white p-4 shadow-sm focus:outline-none border border-neutral-950/10 data-[hover]:border-neutral-950/20  data-[focus]:border-primary-500 data-[focus]:ring-2 data-[focus]:ring-primary-500 dark:bg-neutral-800 dark:border-white/10 dark:data-[hover]:border-white/20'
                      ])}
                    >
                      <span className="flex gap-1.5 flex-1">
                        <BuildingOfficeIcon className="size-5 text-neutral-900 dark:text-white" />
                        <span className="flex flex-col">
                          <span className="block text-base sm:text-sm font-medium text-neutral-900 dark:text-white">
                            Business Account
                          </span>
                          <span className="mt-1 flex items-center text-sm sm:text-xs text-neutral-500 dark:text-neutral-400 group-data-[disabled]:text-neutral-600 dark:group-data-[disabled]:text-neutral-300">
                            An account for a business with commercial policies.
                          </span>
                        </span>
                      </span>
                      <CheckCircleIcon
                        aria-hidden="true"
                        className="h-5 w-5 text-primary-500 dark:text-primary-400 [.group:not([data-checked])_&]:invisible"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-primary-500"
                      />
                    </Headless.Radio>
                    <Headless.Radio
                      value={AccountType.Individual}
                      aria-label="Individual Account"
                      aria-description="An account for an individual."
                      className={clsx([
                        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
                        'group relative flex cursor-pointer rounded-lg bg-white p-4 shadow-sm focus:outline-none border border-neutral-950/10 data-[hover]:border-neutral-950/20  data-[focus]:border-primary-500 data-[focus]:ring-2 data-[focus]:ring-primary-500 dark:bg-neutral-800 dark:border-white/10 dark:data-[hover]:border-white/20'
                      ])}
                    >
                      <span className="flex gap-1.5 flex-1">
                        <UserIcon className="size-5 text-neutral-900 dark:text-white" />
                        <span className="flex flex-col">
                          <span className="block sm:text-sm font-medium text-neutral-900 dark:text-white">
                            Individual Account
                          </span>
                          <span className="mt-1 flex items-center text-sm sm:text-xs text-neutral-500 dark:text-neutral-400 group-data-[disabled]:text-neutral-600 dark:group-data-[disabled]:text-neutral-300">
                            An account for an individual with personal policies.
                          </span>
                        </span>
                      </span>
                      <CheckCircleIcon
                        aria-hidden="true"
                        className="h-5 w-5 text-primary-500 dark:text-primary-400 [.group:not([data-checked])_&]:invisible"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-primary-500"
                      />
                    </Headless.Radio>
                  </div>
                </Headless.RadioGroup>
              </Field>
            </FieldGroup>
          </Fieldset>
          {form?.type === AccountType.Business && (
            <>
              <Fieldset>
                <Legend>Business Details</Legend>
                <Description>
                  Specify the details of the business. This information creates the profile of the
                  business. This information can be different from the account's contact information
                  collected below.
                </Description>
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-6">
                    <Field>
                      <Label>Business Name</Label>
                      <Description>The legal name of the business</Description>
                      <Input
                        value={form?.business?.name || ''}
                        onChange={(e) => {
                          const name = e.currentTarget.value;
                          updateForm(['account', 'name'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  business: prev.business ? { ...prev.business, name } : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'name'])}
                      />
                      {getValidationError(validationErrors, ['account', 'name']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'name'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Business DBA</Label>
                      <Description>The DBA of the business, if any</Description>
                      <Input
                        value={form.business?.dba || ''}
                        onChange={(e) => {
                          const dba = e.currentTarget.value;
                          updateForm(['account', 'dba'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  business: prev.business ? { ...prev.business, dba } : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'dba'])}
                      />
                      {getValidationError(validationErrors, ['account', 'dba']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'dba'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Email Address</Label>
                      <Description>The email address of the business</Description>
                      <Input
                        type="email"
                        value={form.business?.email || ''}
                        onChange={(e) => {
                          const email = e.currentTarget.value;
                          updateForm(['account', 'email'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  business: prev.business ? { ...prev.business, email } : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'email'])}
                      />
                      {getValidationError(validationErrors, ['account', 'email']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'email'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Phone Number</Label>
                      <Description>The phone number of the business</Description>
                      <Input
                        type="tel"
                        value={form.business?.phone || ''}
                        onChange={(e) => {
                          const phone = e.currentTarget.value;
                          updateForm(['account', 'phone'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  business: prev.business ? { ...prev.business, phone } : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'phone'])}
                      />
                      {getValidationError(validationErrors, ['account', 'phone']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'phone'])}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                </FieldGroup>
                <AddressForm
                  addressTitle="Mailing Address"
                  addressDescription="Enter the mailing address of the business"
                  initialValue={{
                    line1: form.business?.mailingAddress?.line1 || '',
                    line2: form.business?.mailingAddress?.line2 || '',
                    city: form.business?.mailingAddress?.city || '',
                    zone: form.business?.mailingAddress?.zone || '',
                    postalCode: form.business?.mailingAddress?.postalCode || ''
                  }}
                  onChange={(update) =>
                    updateFormMulti(
                      [
                        ['account', 'mailingAddress', 'line1'],
                        ['account', 'mailingAddress', 'line2'],
                        ['account', 'mailingAddress', 'city'],
                        ['account', 'mailingAddress', 'zone'],
                        ['account', 'mailingAddress', 'postalCode']
                      ],
                      (prev) =>
                        !prev
                          ? prev
                          : {
                              ...prev,
                              business: prev.business
                                ? {
                                    ...prev.business,
                                    mailingAddress: update(prev.business.mailingAddress)
                                  }
                                : undefined
                            }
                    )
                  }
                  validationErrors={{
                    line1: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'line1'
                    ]),
                    line2: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'line2'
                    ]),
                    city: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'city'
                    ]),
                    zone: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'zone'
                    ]),
                    postalCode: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'postalCode'
                    ])
                  }}
                />
              </Fieldset>
              <Fieldset>
                <Legend>Primary Contact Information</Legend>
                <Description>
                  Specify the primary contact associated with this account. This contact will
                  receive notifications for policies and other account activities, and will be able
                  to log into to the client application.
                </Description>
                <FieldGroup>
                  <Field>
                    <RadioGroup
                      value={contactCreationMode}
                      onChange={(val) => {
                        setContactCreationMode((prev) => (prev === 'new' ? 'existing' : 'new'));
                        updateFormMulti(
                          [
                            ['associateContactId'],
                            ['contact', 'name'],
                            ['contact', 'email'],
                            ['contact', 'phone']
                          ],
                          (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  associateContactId: val === 'new' ? null : '',
                                  contact:
                                    val === 'existing'
                                      ? null
                                      : {
                                          name: '',
                                          email: '',
                                          phone: ''
                                        }
                                }
                        );
                      }}
                    >
                      <RadioField>
                        <Radio value="new" color="sky" />
                        <Label>Create a new contact</Label>
                        <Description>Create a new contact for this account.</Description>
                      </RadioField>
                      <RadioField>
                        <Radio value="existing" color="sky" />
                        <Label>Associate an existing contact</Label>
                        <Description>Associate an existing contact with this account.</Description>
                      </RadioField>
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['contact'])}
                      </ErrorMessage>
                    </RadioGroup>
                  </Field>
                </FieldGroup>
                {contactCreationMode === 'existing' && (
                  <FieldGroup>
                    <Field>
                      <Label>Contact</Label>
                      <Description>
                        Search for and choose the contact to associate with this account.
                      </Description>
                      <Combobox
                        value={form?.associateContactId || ''}
                        disabled={contactsLoading || !contacts?.contacts?.length}
                        placeholder={
                          contactsLoading
                            ? 'Loading...'
                            : !contacts?.contacts?.length
                              ? 'No existing contacts'
                              : 'Search for a contact'
                        }
                        minQueryLength={0}
                        maxOptions={1000}
                        onChange={(contactId) =>
                          updateForm(['associateContactId'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  associateContactId: contactId
                                }
                          )
                        }
                        options={
                          contacts?.contacts?.map((contact) => ({
                            value: contact.id,
                            label: contact.name,
                            description: contact.email
                          })) || []
                        }
                        error={
                          contactsError?.message ||
                          getValidationError(validationErrors, ['associateContactId'])
                        }
                      />
                    </Field>
                  </FieldGroup>
                )}
                {contactCreationMode === 'new' && (
                  <FieldGroup>
                    <div className="grid grid-cols-2 gap-6">
                      <Field>
                        <Label>Contact Name</Label>
                        <Description>
                          The name of the contact associated with this account.
                        </Description>
                        <Input
                          value={form.contact?.name || ''}
                          onChange={(e) => {
                            const name = e.currentTarget.value;
                            updateForm(['contact', 'name'], (prev) =>
                              !prev
                                ? prev
                                : {
                                    ...prev,
                                    contact: prev.contact ? { ...prev.contact, name } : undefined
                                  }
                            );
                          }}
                          invalid={!!getValidationError(validationErrors, ['contact', 'name'])}
                        />
                        {getValidationError(validationErrors, ['contact', 'name']) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, ['contact', 'name'])}
                          </ErrorMessage>
                        )}
                      </Field>
                      <Field>
                        <Label>Email Address</Label>
                        <Description>
                          The email address of the contact associated with this account.
                        </Description>
                        <Input
                          type="email"
                          value={form.contact?.email || ''}
                          onChange={(e) => {
                            const email = e.currentTarget.value;
                            updateForm(['contact', 'email'], (prev) =>
                              !prev
                                ? prev
                                : {
                                    ...prev,
                                    contact: prev.contact ? { ...prev.contact, email } : undefined
                                  }
                            );
                          }}
                          invalid={!!getValidationError(validationErrors, ['contact', 'email'])}
                        />
                        {getValidationError(validationErrors, ['contact', 'email']) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, ['contact', 'email'])}
                          </ErrorMessage>
                        )}
                      </Field>
                      <Field>
                        <Label>Phone Number</Label>
                        <Description>
                          The phone number of the contact associated with this account.
                        </Description>
                        <Input
                          type="tel"
                          value={form.contact?.phone || ''}
                          onChange={(e) => {
                            const phone = e.currentTarget.value;
                            updateForm(['contact', 'phone'], (prev) =>
                              !prev
                                ? prev
                                : {
                                    ...prev,
                                    contact: prev.contact ? { ...prev.contact, phone } : undefined
                                  }
                            );
                          }}
                          invalid={!!getValidationError(validationErrors, ['contact', 'phone'])}
                        />
                        {getValidationError(validationErrors, ['contact', 'phone']) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, ['contact', 'phone'])}
                          </ErrorMessage>
                        )}
                      </Field>
                    </div>
                  </FieldGroup>
                )}
              </Fieldset>
            </>
          )}
          {form?.type === AccountType.Individual && (
            <>
              <Fieldset>
                <Legend>Individual Details</Legend>
                <Description>
                  Specify the details of the individual. This information creates the profile of the
                  individual and serves as the contact information associated with this account.
                </Description>
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-6">
                    <Field>
                      <Label>Individual Name</Label>
                      <Description>The name of the individual</Description>
                      <Input
                        value={form?.individual?.name || ''}
                        onChange={(e) => {
                          const name = e.currentTarget.value;
                          updateForm(['account', 'name'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  individual: prev.individual
                                    ? { ...prev.individual, name }
                                    : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'name'])}
                      />
                      {getValidationError(validationErrors, ['account', 'name']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'name'])}
                        </ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label>Email Address</Label>
                      <Description>The email address of the individual</Description>
                      <Input
                        type="email"
                        value={form.individual?.email || ''}
                        onChange={(e) => {
                          const email = e.currentTarget.value;
                          updateForm(['account', 'email'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  individual: prev.individual
                                    ? { ...prev.individual, email }
                                    : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'email'])}
                      />
                      {getValidationError(validationErrors, ['account', 'email']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'email'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Phone Number</Label>
                      <Description>The phone number of the individual</Description>
                      <Input
                        type="tel"
                        value={form.individual?.phone || ''}
                        onChange={(e) => {
                          const phone = e.currentTarget.value;
                          updateForm(['account', 'phone'], (prev) =>
                            !prev
                              ? prev
                              : {
                                  ...prev,
                                  individual: prev.individual
                                    ? { ...prev.individual, phone }
                                    : undefined
                                }
                          );
                        }}
                        invalid={!!getValidationError(validationErrors, ['account', 'phone'])}
                      />
                      {getValidationError(validationErrors, ['account', 'phone']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['account', 'phone'])}
                        </ErrorMessage>
                      )}
                    </Field>
                  </div>
                </FieldGroup>
                <AddressForm
                  addressTitle="Mailing Address"
                  addressDescription="Enter the mailing address of the individual"
                  initialValue={{
                    line1: form.individual?.mailingAddress?.line1 || '',
                    line2: form.individual?.mailingAddress?.line2 || '',
                    city: form.individual?.mailingAddress?.city || '',
                    zone: form.individual?.mailingAddress?.zone || '',
                    postalCode: form.individual?.mailingAddress?.postalCode || ''
                  }}
                  onChange={(update) =>
                    updateFormMulti(
                      [
                        ['account', 'mailingAddress', 'line1'],
                        ['account', 'mailingAddress', 'line2'],
                        ['account', 'mailingAddress', 'city'],
                        ['account', 'mailingAddress', 'zone'],
                        ['account', 'mailingAddress', 'postalCode']
                      ],
                      (prev) =>
                        !prev
                          ? prev
                          : {
                              ...prev,
                              individual: prev.individual
                                ? {
                                    ...prev.individual,
                                    mailingAddress: update(prev.individual.mailingAddress)
                                  }
                                : undefined
                            }
                    )
                  }
                  validationErrors={{
                    line1: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'line1'
                    ]),
                    line2: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'line2'
                    ]),
                    city: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'city'
                    ]),
                    zone: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'zone'
                    ]),
                    postalCode: getValidationError(validationErrors, [
                      'account',
                      'mailingAddress',
                      'postalCode'
                    ])
                  }}
                />
              </Fieldset>
            </>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!form}
            loading={navigation.state === 'submitting'}
          >
            Create Account
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const AccountEditPage = () => {
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | { data: Promise<{ account?: AccountFieldsFragment }> }
    | undefined;

  return (
    <MountableDialog onClose={() => navigate(-1)} size="2xl">
      <DialogTitle>Edit account</DialogTitle>
      <DialogDescription>Edit the account details.</DialogDescription>
      <React.Suspense>
        <Await resolve={loader?.data}>
          {(data) => data?.account && <AccountEditForm account={data.account} />}
        </Await>
      </React.Suspense>
    </MountableDialog>
  );
};

const AccountEditForm = ({ account }: { account?: AccountFieldsFragment }) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<UpdateAccountMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<UpdateAccountInput>();
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (account) {
      // No editing the contact here, only the account
      setForm({
        business: account.account.__typename === 'BusinessAccount' ? account.account : undefined,
        individual: account.account.__typename === 'IndividualAccount' ? account.account : undefined
      });
    }
  }, [account]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(form, { method: 'PUT', encType: 'application/json' });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
        {!!form && account?.type === AccountType.Business && (
          <>
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-2 gap-6">
                  <Field>
                    <Label>Business Name</Label>
                    <Description>The legal name of the business</Description>
                    <Input
                      value={form?.business?.name || ''}
                      onChange={(e) => {
                        const name = e.currentTarget.value;
                        updateForm(['account', 'name'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                business: prev.business ? { ...prev.business, name } : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'name'])}
                    />
                    {getValidationError(validationErrors, ['account', 'name']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'name'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Business DBA</Label>
                    <Description>The DBA of the business, if any</Description>
                    <Input
                      value={form.business?.dba || ''}
                      onChange={(e) => {
                        const dba = e.currentTarget.value;
                        updateForm(['account', 'dba'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                business: prev.business ? { ...prev.business, dba } : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'dba'])}
                    />
                    {getValidationError(validationErrors, ['account', 'dba']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'dba'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Email Address</Label>
                    <Description>The email address of the business</Description>
                    <Input
                      type="email"
                      value={form.business?.email || ''}
                      onChange={(e) => {
                        const email = e.currentTarget.value;
                        updateForm(['account', 'email'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                business: prev.business ? { ...prev.business, email } : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'email'])}
                    />
                    {getValidationError(validationErrors, ['account', 'email']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'email'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Phone Number</Label>
                    <Description>The phone number of the business</Description>
                    <Input
                      type="tel"
                      value={form.business?.phone || ''}
                      onChange={(e) => {
                        const phone = e.currentTarget.value;
                        updateForm(['account', 'phone'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                business: prev.business ? { ...prev.business, phone } : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'phone'])}
                    />
                    {getValidationError(validationErrors, ['account', 'phone']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'phone'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
              <AddressForm
                addressTitle="Mailing Address"
                addressDescription="Enter the mailing address of the business"
                initialValue={{
                  line1: form.business?.mailingAddress?.line1 || '',
                  line2: form.business?.mailingAddress?.line2 || '',
                  city: form.business?.mailingAddress?.city || '',
                  zone: form.business?.mailingAddress?.zone || '',
                  postalCode: form.business?.mailingAddress?.postalCode || ''
                }}
                onChange={(update) =>
                  updateFormMulti(
                    [
                      ['account', 'mailingAddress', 'line1'],
                      ['account', 'mailingAddress', 'line2'],
                      ['account', 'mailingAddress', 'city'],
                      ['account', 'mailingAddress', 'zone'],
                      ['account', 'mailingAddress', 'postalCode']
                    ],
                    (prev) =>
                      !prev
                        ? prev
                        : {
                            ...prev,
                            business: prev.business
                              ? {
                                  ...prev.business,
                                  mailingAddress: update(prev.business.mailingAddress)
                                }
                              : undefined
                          }
                  )
                }
                validationErrors={{
                  line1: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'line1'
                  ]),
                  line2: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'line2'
                  ]),
                  city: getValidationError(validationErrors, ['account', 'mailingAddress', 'city']),
                  zone: getValidationError(validationErrors, ['account', 'mailingAddress', 'zone']),
                  postalCode: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'postalCode'
                  ])
                }}
              />
            </Fieldset>
          </>
        )}
        {!!form && account?.type === AccountType.Individual && (
          <>
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-2 gap-6">
                  <Field>
                    <Label>Individual Name</Label>
                    <Description>The name of the individual</Description>
                    <Input
                      value={form?.individual?.name || ''}
                      onChange={(e) => {
                        const name = e.currentTarget.value;
                        updateForm(['account', 'name'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                individual: prev.individual
                                  ? { ...prev.individual, name }
                                  : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'name'])}
                    />
                    {getValidationError(validationErrors, ['account', 'name']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'name'])}
                      </ErrorMessage>
                    )}
                  </Field>

                  <Field>
                    <Label>Email Address</Label>
                    <Description>The email address of the individual</Description>
                    <Input
                      type="email"
                      value={form.individual?.email || ''}
                      onChange={(e) => {
                        const email = e.currentTarget.value;
                        updateForm(['account', 'email'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                individual: prev.individual
                                  ? { ...prev.individual, email }
                                  : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'email'])}
                    />
                    {getValidationError(validationErrors, ['account', 'email']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'email'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Phone Number</Label>
                    <Description>The phone number of the individual</Description>
                    <Input
                      type="tel"
                      value={form.individual?.phone || ''}
                      onChange={(e) => {
                        const phone = e.currentTarget.value;
                        updateForm(['account', 'phone'], (prev) =>
                          !prev
                            ? prev
                            : {
                                ...prev,
                                individual: prev.individual
                                  ? { ...prev.individual, phone }
                                  : undefined
                              }
                        );
                      }}
                      invalid={!!getValidationError(validationErrors, ['account', 'phone'])}
                    />
                    {getValidationError(validationErrors, ['account', 'phone']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['account', 'phone'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
              <AddressForm
                addressTitle="Mailing Address"
                addressDescription="Enter the mailing address of the individual"
                initialValue={{
                  line1: form.individual?.mailingAddress?.line1 || '',
                  line2: form.individual?.mailingAddress?.line2 || '',
                  city: form.individual?.mailingAddress?.city || '',
                  zone: form.individual?.mailingAddress?.zone || '',
                  postalCode: form.individual?.mailingAddress?.postalCode || ''
                }}
                onChange={(update) =>
                  updateFormMulti(
                    [
                      ['account', 'mailingAddress', 'line1'],
                      ['account', 'mailingAddress', 'line2'],
                      ['account', 'mailingAddress', 'city'],
                      ['account', 'mailingAddress', 'zone'],
                      ['account', 'mailingAddress', 'postalCode']
                    ],
                    (prev) =>
                      !prev
                        ? prev
                        : {
                            ...prev,
                            individual: prev.individual
                              ? {
                                  ...prev.individual,
                                  mailingAddress: update(prev.individual.mailingAddress)
                                }
                              : undefined
                          }
                  )
                }
                validationErrors={{
                  line1: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'line1'
                  ]),
                  line2: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'line2'
                  ]),
                  city: getValidationError(validationErrors, ['account', 'mailingAddress', 'city']),
                  zone: getValidationError(validationErrors, ['account', 'mailingAddress', 'zone']),
                  postalCode: getValidationError(validationErrors, [
                    'account',
                    'mailingAddress',
                    'postalCode'
                  ])
                }}
              />
            </Fieldset>
          </>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          type="button"
          color="white"
          disabled={navigation.state === 'submitting'}
          onClick={() => navigate(-1)}
        >
          Close
        </Button>
        <Button
          type="submit"
          color="sky"
          disabled={!form}
          loading={navigation.state === 'submitting'}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Form>
  );
};

export const AccountPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ account?: AccountFieldsFragment & AccountContactFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
      <Await resolve={loader?.data}>
        {(data) =>
          !!data?.account && (
            <>
              <div className="lg:flex lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="m-0 text-2xl font-semibold text-neutral-900 dark:text-white sm:truncate sm:text-3xl sm:tracking-tight">
                    {data.account?.account.name}
                  </h2>
                  <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                    {data.account?.type === AccountType.Business && (
                      <div className="mt-2 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                        <UserIcon
                          aria-hidden="true"
                          className="mr-1.5 size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                        />
                        {data.account?.contacts[0].name}
                      </div>
                    )}
                    <div className="mt-2 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                      <CalendarIcon
                        aria-hidden="true"
                        className="mr-1.5 size-5 shrink-0 text-neutral-400 dark:text-neutral-500"
                      />
                      Created on{' '}
                      {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                        new Date(data.account?.createdAt || '')
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                  <span className="hidden sm:block">
                    <Button
                      type="button"
                      color="sky"
                      href={`/app/accounts/${data.account?.id}/edit`}
                    >
                      <PencilIcon
                        aria-hidden="true"
                        className="-ml-0.5 mr-1.5 size-5 text-white-400"
                      />
                      Edit
                    </Button>
                  </span>
                </div>
              </div>
              <PageNavigation
                routes={[
                  {
                    path: 'overview',
                    title: 'Overview'
                  },
                  {
                    path: 'policies',
                    title: 'Policies'
                  },
                  {
                    path: 'certificates',
                    title: 'Certificates'
                  },
                  {
                    path: 'service',
                    title: 'Service Requests'
                  }
                ]}
              />
            </>
          )
        }
      </Await>
    </React.Suspense>
  );
};

export const AccountOverviewPage = () => {
  const loader = useRouteLoaderData('accounts/:id') as
    | {
        data: Promise<{
          account?: AccountFieldsFragment & AccountContactFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {(data) =>
          !!data?.account && (
            <div className="space-y-8">
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                    <div>
                      <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                        Contact overview
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Basic details about the contact for this account.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="@container">
                  <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Contact Name
                      </dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                        <span>{data.account?.contacts[0].name}</span>
                      </dd>
                    </div>
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Email Address
                      </dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                        <span>{data.account?.contacts[0].email}</span>
                      </dd>
                    </div>
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Phone Number
                      </dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                        <span>{data.account?.contacts[0].phone}</span>
                      </dd>
                    </div>
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Account Since
                      </dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                        <span>
                          {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                            new Date(data.account?.createdAt || '')
                          )}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {data.account?.type === AccountType.Business && (
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                      <div>
                        <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                          Business details
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          Details about the business associated with the client.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="@container">
                    <dl className="divide-y divide-neutral-100 dark:divide-neutral-700">
                      <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                          Business Name
                        </dt>
                        <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                          <span>{data.account?.account.name}</span>
                        </dd>
                      </div>
                      <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                          Mailing Address
                        </dt>
                        <dd className="mt-1 text-sm leading-6 text-neutral-900 dark:text-white sm:col-span-2 sm:mt-0">
                          <span>
                            {data.account?.account.mailingAddress.line1}
                            {data.account?.account.mailingAddress.line2 &&
                              `, ${data.account?.account.mailingAddress.line2}`}
                            {', '}
                            {data.account?.account.mailingAddress.city},{' '}
                            {data.account?.account.mailingAddress.zone}{' '}
                            {data.account?.account.mailingAddress.postalCode}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const policyStatuses = {
  [PolicyState.Binding]:
    'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-500/10 ring-primary-600/20',
  [PolicyState.Inforce]:
    'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 ring-green-600/20',
  [PolicyState.Expired]:
    'text-neutral-600 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-900 ring-neutral-500/10',
  [PolicyState.Canceled]:
    'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 ring-red-600/10'
};

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const AccountPoliciesPage = () => {
  const loader = useRouteLoaderData('accounts/:id') as
    | {
        data: Promise<{
          account?: AccountFieldsFragment & AccountBusinessPolicyFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {(data) =>
          !!data?.account && (
            <>
              {data?.account?.type === AccountType.Business && (
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                      <div>
                        <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                          Business Insurance Policies
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          Details about the policies associated with this client.
                        </p>
                      </div>
                      {!!data.account?.businessPolicies?.length && (
                        <div className="flex-shrink-0 mt-2 sm:mt-0">
                          <Button
                            href={`/app/policies/new?accountId=${data.account?.id}`}
                            type="button"
                            color="sky"
                          >
                            Add Policy
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      {!data.account?.businessPolicies?.length && (
                        <div className="text-center p-8">
                          <DocumentPlusIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                          <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                            No policies
                          </h4>
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            Get started by logging a new policy.
                          </p>
                          <div className="mt-6">
                            <Button
                              href={`/app/policies/new?accountId=${data.account?.id}`}
                              type="button"
                              color="sky"
                            >
                              Add Policy
                            </Button>
                          </div>
                        </div>
                      )}
                      {!!data.account?.businessPolicies?.length && (
                        <div className="inline-block min-w-full py-2 align-middle">
                          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                            <thead className="bg-neutral-50 dark:bg-neutral-900">
                              <tr>
                                <th
                                  scope="col"
                                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-white sm:pl-6 lg:pl-8"
                                >
                                  Coverage
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                                >
                                  Status
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                                >
                                  Carrier
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                                >
                                  Expiration Date
                                </th>
                                <th
                                  scope="col"
                                  className="relative py-3.5 pl-3 pr-4 sm:pr-6 lg:pr-8"
                                >
                                  <span className="sr-only">Edit</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                              {data.account.businessPolicies.map((policy) => (
                                <tr key={policy.id}>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                                    <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                      {getDisplayInsuranceType(policy.type)}
                                    </div>
                                    <div className="text-xs mt-1 text-neutral-500 dark:text-neutral-400">
                                      {policy.policyNumber || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                    <div
                                      className={clsx(
                                        policyStatuses[policy.state],
                                        'inline-flex rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset'
                                      )}
                                    >
                                      {titleCase(policy.state)}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                    {policy.carrier.name}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                    {!policy.expiresAt ||
                                    isNaN(new Date(policy.expiresAt).getTime())
                                      ? 'N/A'
                                      : new Intl.DateTimeFormat('en-US').format(
                                          new Date(policy.expiresAt)
                                        )}
                                  </td>
                                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 lg:pr-8">
                                    <Link
                                      to={`/app/policies/${policy.id}/overview`}
                                      className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 cursor-pointer"
                                    >
                                      View
                                      <span className="sr-only">
                                        , {getDisplayInsuranceType(policy.type)}
                                      </span>
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {data?.account?.type === AccountType.Individual && (
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                      <div>
                        <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                          Personal Insurance Policies
                        </h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          Details about the policies associated with this client.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      {!data.account?.businessPolicies?.length && (
                        <div className="text-center p-8">
                          <DocumentPlusIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                          <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                            No policies
                          </h4>
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            Personal policies are not supported yet. Reach out to the Oyster support
                            team for more information.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )
        }
      </Await>
    </React.Suspense>
  );
};

export const AccountCertificatesPage = () => {
  const outlet = useOutlet();
  // const location = useLocation();
  const loader = useRouteLoaderData('accounts/:id') as
    | {
        data: Promise<{
          account?: AccountFieldsFragment &
            AccountCertificateOfLiabilityInsuranceFieldsFragment &
            AccountBusinessPolicyFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <>
      <React.Suspense>
        <Await resolve={loader?.data}>
          {(data) =>
            !!data?.account && (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div>
                      <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                        Certificates of Insurance
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Details about the certificates of insurance associated with this account.
                      </p>
                    </div>
                    {!!data.account.certificatesOfLiabilityInsurance?.length &&
                      !!data.account.businessPolicies?.length && (
                        <div className="flex-shrink-0 mt-2 sm:mt-0">
                          <Button
                            href={`/app/accounts/${data.account.id}/certificates/new`}
                            type="button"
                            color="sky"
                          >
                            Add Certificate
                          </Button>
                        </div>
                      )}
                  </div>
                </div>
                <div className="px-4 sm:px-6">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    {!data.account.certificatesOfLiabilityInsurance?.length &&
                      !!data.account.businessPolicies?.length && (
                        <div className="text-center p-8">
                          <DocumentPlusIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                          <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                            No certificates
                          </h4>
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            Get started by creating a new certificate of liability insurance.
                          </p>
                          <div className="mt-6">
                            <Button
                              href={`/app/accounts/${data.account.id}/certificates/new`}
                              type="button"
                              color="sky"
                            >
                              Add Certificate
                            </Button>
                          </div>
                        </div>
                      )}
                    {!data.account.certificatesOfLiabilityInsurance?.length &&
                      !data.account.businessPolicies?.length && (
                        <div className="text-center p-8">
                          <DocumentPlusIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                          <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                            No policies
                          </h4>
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            To create a certificate of liability insurance, start by logging a
                            policy.
                          </p>
                          <div className="mt-6">
                            <Button
                              href={`/app/policies/new?accountId=${data.account?.id}`}
                              type="button"
                              color="sky"
                            >
                              Add Policy
                            </Button>
                          </div>
                        </div>
                      )}
                    {data.account.certificatesOfLiabilityInsurance?.length > 0 && (
                      <div className="inline-block min-w-full py-2 align-middle">
                        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                          <thead className="bg-neutral-50 dark:bg-neutral-900">
                            <tr>
                              <th
                                scope="col"
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-white sm:pl-6 lg:pl-8"
                              >
                                Certificate Holder
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                              >
                                Certificate Number
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                              >
                                Issued Date
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                              >
                                Actions
                              </th>
                              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 lg:pr-8">
                                <span className="sr-only">Edit</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                            {data.account.certificatesOfLiabilityInsurance.map((certificate) => (
                              <tr key={certificate.id}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                                  <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {certificate.holder.name}
                                  </div>
                                  <div className="text-xs mt-1 text-neutral-500 dark:text-neutral-400">
                                    {certificate.holder.address.city},{' '}
                                    {certificate.holder.address.zone}{' '}
                                    {certificate.holder.address.postalCode}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                  {certificate.certificateNumber}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                  {!certificate.createdAt ||
                                  isNaN(new Date(certificate.createdAt).getTime())
                                    ? 'N/A'
                                    : new Intl.DateTimeFormat('en-US').format(
                                        new Date(certificate.createdAt)
                                      )}
                                </td>
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                                  <div className="flex">
                                    <Button
                                      type="button"
                                      plain
                                      href={`/app/accounts/${data.account?.id}/certificates/${certificate.id}`}
                                      className="rounded-full p-1"
                                    >
                                      <EyeIcon className="size-8 text-neutral-400 dark:text-neutral-500" />
                                    </Button>
                                    <Button
                                      type="button"
                                      plain
                                      target="_blank"
                                      href={certificate.downloadUrl || '#'}
                                      className="rounded-full p-1 cursor-pointer"
                                    >
                                      <CloudArrowDownIcon className="size-8 text-neutral-400 dark:text-neutral-500" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, {})}
      </AnimatePresence>
    </>
  );
};

export const AccountCertificatePage = () => {
  const navigate = useNavigate();
  const loader = useRouteLoaderData('accounts/:id/certificates/:certificateId') as
    | {
        data: Promise<{
          certificate?: CertificateOfLiabilityInsuranceFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <MountableDialog onClose={() => navigate(-1)} size="4xl">
      <DialogTitle>View certificate</DialogTitle>
      <React.Suspense
        fallback={
          <TransitionDiv>
            <Spinner color="neutral" size="size-8" />
          </TransitionDiv>
        }
      >
        <Await resolve={loader?.data}>
          {(data) =>
            !!data?.certificate && (
              <>
                <DialogDescription>
                  {data.certificate.holder.name}, {data.certificate.holder.address.city},{' '}
                  {data.certificate.holder.address.zone}{' '}
                  {data.certificate.holder.address.postalCode}
                </DialogDescription>
                <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
                  <iframe
                    src={data.certificate.downloadUrl}
                    className="w-full h-full min-h-[500px]"
                  />
                </DialogBody>
              </>
            )
          }
        </Await>
      </React.Suspense>
      <DialogActions>
        <Button type="button" color="white" onClick={() => navigate(-1)}>
          Close
        </Button>
      </DialogActions>
    </MountableDialog>
  );
};

export const AccountCertificateCreatePage = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateCertificateMutation>;
  const loader = useRouteLoaderData('accounts/:id') as
    | {
        data: Promise<{ account?: AccountFieldsFragment }>;
      }
    | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateCertificateInput>({
    accountId: '',
    additionalInsured: false,
    descriptionOfOperations: '',
    holder: {
      name: '',
      address: {
        line1: '',
        city: '',
        zone: '',
        postalCode: ''
      }
    }
  });
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(
      { ...form, accountId: (await loader?.data)?.account?.id || '' },
      { method: 'POST', encType: 'application/json' }
    );
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="xl">
      <DialogTitle>Create a new certificate</DialogTitle>
      <DialogDescription>
        Create a new certificate of liability insurance for this account.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200 dark:divide-neutral-700">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Certificate Holder</Label>
                <Input
                  type="text"
                  placeholder="ACME Corporation, Inc."
                  value={form.holder.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    updateForm(['holder', 'name'], (prev) => ({
                      ...prev,
                      holder: { ...prev.holder, name }
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['holder', 'name'])}
                />
                {!!getValidationError(validationErrors, ['holder', 'name']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['holder', 'name'])}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
            <AddressForm
              addressTitle="Certificate Holder Address"
              addressDescription="Enter the address of the certificate holder."
              initialValue={{
                line1: form.holder.address.line1,
                line2: form.holder.address.line2,
                city: form.holder.address.city,
                zone: form.holder.address.zone,
                postalCode: form.holder.address.postalCode
              }}
              onChange={(update) =>
                updateFormMulti(
                  [
                    ['holder', 'address', 'line1'],
                    ['holder', 'address', 'line2'],
                    ['holder', 'address', 'city'],
                    ['holder', 'address', 'zone'],
                    ['holder', 'address', 'postalCode']
                  ],
                  (prev) => ({
                    ...prev,
                    holder: { ...prev.holder, address: update(prev.holder.address) }
                  })
                )
              }
              validationErrors={{
                line1: getValidationError(validationErrors, ['holder', 'address', 'line1']),
                line2: getValidationError(validationErrors, ['holder', 'address', 'line2']),
                city: getValidationError(validationErrors, ['holder', 'address', 'city']),
                zone: getValidationError(validationErrors, ['holder', 'address', 'zone']),
                postalCode: getValidationError(validationErrors, [
                  'holder',
                  'address',
                  'postalCode'
                ])
              }}
            />
            <FieldGroup>
              <CheckboxField>
                <Label>The certificate holder is also an additional insured.</Label>
                <Checkbox
                  color="sky"
                  checked={form.additionalInsured}
                  onChange={() =>
                    updateForm(['additionalInsured'], (prev) => ({
                      ...prev,
                      additionalInsured: !prev.additionalInsured
                    }))
                  }
                />
                {!!getValidationError(validationErrors, ['additionalInsured']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['additionalInsured'])}
                  </ErrorMessage>
                )}
              </CheckboxField>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Description of Operations</Label>
                <Textarea
                  rows={3}
                  value={form.descriptionOfOperations}
                  onChange={(e) => {
                    const descriptionOfOperations = e.target.value;
                    updateForm(['descriptionOfOperations'], (prev) => ({
                      ...prev,
                      descriptionOfOperations
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['descriptionOfOperations'])}
                />
                {!!getValidationError(validationErrors, ['descriptionOfOperations']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['descriptionOfOperations'])}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button type="submit" color="sky" loading={navigation.state === 'submitting'}>
            Create Certificate
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const AccountServiceRequestsPage = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const loader = useRouteLoaderData('accounts/:id') as
    | {
        data: Promise<{
          account?: AccountFieldsFragment & AccountServiceRequestFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <>
      <React.Suspense>
        <Await resolve={loader?.data}>
          {(data) =>
            !!data?.account && (
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div>
                      <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900 dark:text-white">
                        Service Requests
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Customer service requests associated with this account.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-4 sm:px-6">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    {!data.account.serviceRequests?.length && (
                      <div className="text-center p-8">
                        <InboxIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                        <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                          No service requests
                        </h4>
                        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                          You're all set! This account has not made any service requests.
                        </p>
                      </div>
                    )}
                    {data.account.serviceRequests?.length > 0 && (
                      <div className="inline-block min-w-full py-2 align-middle">
                        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                          <thead className="bg-neutral-50 dark:bg-neutral-900">
                            <tr>
                              <th
                                scope="col"
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-white sm:pl-6 lg:pl-8"
                              >
                                Request Type
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                              >
                                Status
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                              >
                                Last Updated
                              </th>
                              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 lg:pr-8">
                                <span className="sr-only">Edit</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                            {data.account.serviceRequests.map((request) => (
                              <tr key={request.id}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                                  <div className="text-sm font-medium text-neutral-900 dark:text-white">
                                    {titleCase(request.type)}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                  {request.state}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                                  {!request.updatedAt ||
                                  isNaN(new Date(request.updatedAt).getTime())
                                    ? 'N/A'
                                    : new Intl.DateTimeFormat('en-US').format(
                                        new Date(request.updatedAt)
                                      )}
                                </td>
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 lg:pr-8">
                                  <NavLink
                                    to={`/app/servicing/${request.id}/overview`}
                                    className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 cursor-pointer"
                                  >
                                    View
                                  </NavLink>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </>
  );
};
